// What to show a rider as "Refund amount" on the cancel preview.
//
// THE ABSENT REFUND LINE IS A FULL REFUND, NOT ZERO.
//
// BPPs quote cancellations differently. KMRL returns an explicit `refund` (and
// `charges`); BMRCL returns a bare quote with only a BASE_FARE line and no REFUND
// line at all. The backend already encodes this: RefundOnCancel derives the amount
// as |REFUND| − charges with **0 meaning "refund the full captured amount"** — see
// TestRefundOnCancel_AbsentBreakup_FullRefundSentinel, which asserts that a bare
// cancellation publishes amount_paise=0 precisely as that sentinel. A genuine 100%
// cancellation fee is a different case and publishes no refund event at all
// (TestRefundOnCancel_HundredPercentFee_SkipsPublish).
//
// The screen used to render `cancellation.refund?.value ?? '0'`, so a BMRCL rider
// was told "Refund amount ₹0" on a cancellation that in fact refunds them in full.
// On a payments screen that is the dangerous direction to be wrong in: it invites
// someone to keep a ticket they meant to cancel.
import type { Cancellation, Money, Ticket } from './types';

/**
 * Resolve the refund to display.
 *
 * - operator quoted a refund  → that value (this includes an explicit zero, which
 *   really does mean "you get nothing back")
 * - operator quoted no refund → the captured amount, i.e. a full refund
 */
export function displayRefund(
  cancellation: Pick<Cancellation, 'refund'> | undefined,
  ticket: Pick<Ticket, 'fare' | 'paidAmount'>
): Money {
  const quoted = cancellation?.refund;
  if (quoted) return quoted;
  // paidAmount is what actually left the rider's account when a coupon applied;
  // fare is the operator's price. Refunds return the captured amount.
  return ticket.paidAmount ?? ticket.fare;
}

/** True when the refund is a full return of what the rider paid (for copy/emphasis). */
export function isFullRefund(
  cancellation: Pick<Cancellation, 'refund'> | undefined,
  ticket: Pick<Ticket, 'fare' | 'paidAmount'>
): boolean {
  const captured = ticket.paidAmount ?? ticket.fare;
  return displayRefund(cancellation, ticket).value === captured.value;
}
